import Image from "next/image"
import { type ChangeEvent, useEffect, useState } from "react"
import {
    Button,
    type ButtonProps,
    Card,
    Grid,
    Radio,
    RadioOption,
    type RadioValue,
    Stack,
    Typography,
} from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { AsideIllustration } from "@/components/molecules/AsideIllustration"
import { DashboardLayout } from "@/features/common/dashboard/components/DashboardLayout"
import { type FragmentOf, graphql, readFragment } from "@/graphql"
import { meterPowerAnalysisRoutesPaths } from "@/routes/meterPowerAnalysis"

const graphQLFragments = {
    agreement: graphql(`
        fragment MeterPowerAnalysisDashboardCard_Agreement on AgreementType {
            product {
                code
            }
            supplyPoint {
                meterPoint {
                    __typename
                    id
                    ... on ElectricityMeterPoint {
                        isSmartMeter
                        isThreePhase
                    }
                }
            }
        }
    `),
}

type MeterPowerAnalysisDashboardCardProps = {
    agreements: FragmentOf<typeof graphQLFragments.agreement>[]
    buttonProps?: Omit<ButtonProps, "href">
}

export function MeterPowerAnalysisDashboardCard(props: MeterPowerAnalysisDashboardCardProps) {
    const t = useTranslations("MeterPowerAnalysisDashboardCard")

    const agreements = readFragment(graphQLFragments.agreement, props.agreements)

    // Filter electricity active agreements with a smartMeter that is not threePhase
    const eligibleAgreements = agreements.filter(
        (agreement) =>
            agreement.supplyPoint?.meterPoint?.__typename === "ElectricityMeterPoint" &&
            agreement.supplyPoint.meterPoint.isSmartMeter &&
            !agreement.supplyPoint.meterPoint.isThreePhase
    )

    const [selectedAgreement, setSelectedAgreement] = useState(eligibleAgreements[0])

    useEffect(() => {
        setSelectedAgreement(eligibleAgreements[0])
    }, [eligibleAgreements])

    if (
        !selectedAgreement ||
        !selectedAgreement.product?.code ||
        !selectedAgreement.supplyPoint?.meterPoint?.id
    ) {
        return null
    }

    const handleChange = (_event: ChangeEvent<HTMLInputElement>, value: RadioValue) => {
        const agreement = eligibleAgreements.find((a) => a.supplyPoint?.meterPoint?.id === value)

        if (agreement) {
            setSelectedAgreement(agreement)
        }
    }

    return (
        <DashboardLayout.Section>
            <Card
                borderless
                theme="secondary900"
            >
                <Stack
                    gap="md"
                    flexDirection="column"
                >
                    <Typography
                        textStyle="h5"
                        component="p"
                        textAlign={{ base: "center", md: "left" }}
                    >
                        {t("title")}
                    </Typography>

                    <Stack
                        justifyContent="center"
                        gap="lg"
                        flexDirection={{ base: "column", md: "row" }}
                    >
                        <AsideIllustration
                            reverse
                            image={
                                <Image
                                    src="/assets/images/illustrations/opti-compteur.svg"
                                    alt={t("imageAlt")}
                                    width={382}
                                    height={135}
                                />
                            }
                        >
                            <Grid
                                gap="md"
                                fullWidth
                            >
                                <Typography textStyle="body2">{t("description")}</Typography>

                                {eligibleAgreements.length > 1 && (
                                    <Radio
                                        name="meterPointId"
                                        onChange={handleChange}
                                    >
                                        {eligibleAgreements.map((agreement) =>
                                            agreement.supplyPoint?.meterPoint?.id ? (
                                                <RadioOption
                                                    key={agreement.supplyPoint.meterPoint.id}
                                                    value={agreement.supplyPoint.meterPoint.id}
                                                >
                                                    {t("meterPointId", {
                                                        meterPointId:
                                                            agreement.supplyPoint.meterPoint.id,
                                                    })}
                                                </RadioOption>
                                            ) : null
                                        )}
                                    </Radio>
                                )}

                                <Stack justifyContent={{ base: "center", md: "left" }}>
                                    <Button
                                        href={meterPowerAnalysisRoutesPaths.fromDashboard.resolve({
                                            queryParams: {
                                                productCode: selectedAgreement.product.code,
                                                meterPointId:
                                                    selectedAgreement.supplyPoint.meterPoint.id,
                                            },
                                        })}
                                        {...props.buttonProps}
                                    >
                                        {t("callToAction")}
                                    </Button>
                                </Stack>
                            </Grid>
                        </AsideIllustration>
                    </Stack>
                </Stack>
            </Card>
        </DashboardLayout.Section>
    )
}

MeterPowerAnalysisDashboardCard.messages = ["MeterPowerAnalysisDashboardCard"] as const
MeterPowerAnalysisDashboardCard.fragments = graphQLFragments
